/**
 * Studio course-settings API client: schedule & details, grading, certificates.
 *
 * Separate from studio.ts because these three endpoints share a set of hazards that
 * nothing else in the authoring API has, and every one of them is silent:
 *
 * 1. CAPABILITY FLAGS. `course_settings` reports which fields this deployment will
 *    accept (`about_page_editable`, `licensing_enabled`, …). They are not hints. A
 *    field whose flag is false is either ignored on save or rejected outright, so a UI
 *    that renders it teaches authors to enter values that never persist.
 *
 * 2. FULL-OBJECT WRITES. `CourseDetails.update_from_json` reads `jsondict['overview']`
 *    and `jsondict['intro_video']` WITHOUT a guard, and treats an ABSENT date key as
 *    "set this date to null". A tidy PATCH-style payload therefore either 500s or
 *    quietly wipes the course's start, end and enrolment dates. The same is true of
 *    grading: `CourseGradingModel.update_from_json` indexes all four of `graders`,
 *    `grade_cutoffs`, `grace_period` and `minimum_grade_credit`. Both writers here send
 *    the whole object, always.
 *
 * 3. ENUM LEAKAGE. `certificates_display_behavior` is a `(str, Enum)` member serialised
 *    through a DRF CharField, which on Python 3.11+ stringifies to
 *    "CertificatesDisplayBehaviors.END" rather than "end". Echoing that back sets an
 *    invalid value, so reads are normalised on the way in.
 *
 * Shapes verified against the running CMS at release/verawood.1 — see
 * cms/djangoapps/contentstore/rest_api/v1/serializers/{settings,course_details,grading,
 * certificates}.py — not from the MFE's TypeScript, which camel-cases everything.
 */

import { ApiError, openedx, request } from './openedx';

const CONTENTSTORE = '/api/contentstore/v1';

/** Course keys contain ':' and '+', both of which change meaning in a path segment. */
const seg = (courseKey: string) => encodeURIComponent(courseKey);

/* ------------------------------------------------------- schedule & details ------- */

export interface PrerequisiteCourse {
  course_key: string;
  display_name: string;
  lms_link: string;
  number: string;
  org: string;
  rerun_link: string;
  run: string;
  url: string;
}

/**
 * Context for the schedule & details page.
 *
 * Everything named `*_editable` / `*_enabled` / `is_*` is a capability flag; see the
 * file header. `credit_requirements` and `possible_pre_requisite_courses` are only
 * present when their feature is on, hence optional.
 */
export interface CourseSettings {
  about_page_editable: boolean;
  can_show_certificate_available_date_field: boolean;
  course_display_name: string;
  course_display_name_with_default: string;
  credit_eligibility_enabled: boolean;
  credit_requirements?: Record<string, unknown>;
  enable_extended_course_details: boolean;
  enrollment_end_editable: boolean;
  is_credit_course: boolean;
  is_entrance_exams_enabled: boolean;
  is_prerequisite_courses_enabled: boolean;
  /** Pairs of [code, label]. DRF types this as a list of lists, so it is not a tuple. */
  language_options: string[][];
  lms_link_for_about_page: string;
  licensing_enabled: boolean;
  marketing_enabled: boolean;
  mfe_proctored_exam_settings_url?: string | null;
  platform_name: string;
  possible_pre_requisite_courses?: PrerequisiteCourse[];
  short_description_editable: boolean;
  show_min_grade_warning: boolean;
  sidebar_html_enabled: boolean;
  /** Verified-track upgrade deadline. Read-only here; set by enrolment modes. */
  upgrade_deadline: string | null;
}

export interface Instructor {
  name?: string;
  title?: string;
  organization?: string;
  image?: string;
  bio?: string;
}

export type CertificatesDisplayBehaviour = 'end' | 'end_with_date' | 'early_no_info';

export interface CourseDetails {
  about_sidebar_html: string | null;
  banner_image_asset_path: string;
  banner_image_name: string;
  certificate_available_date: string | null;
  certificates_display_behavior: string | null;
  /** The course NUMBER, not the course key — the key is only ever in the URL. */
  course_id: string;
  course_image_asset_path: string;
  course_image_name: string;
  description: string;
  duration: string;
  effort: string | null;
  end_date: string | null;
  enrollment_end: string | null;
  enrollment_start: string | null;
  /** A STRING, not a boolean: 'true' when an entrance exam exists, '' when not. */
  entrance_exam_enabled: string;
  entrance_exam_id: string;
  entrance_exam_minimum_score_pct: string;
  has_changes?: boolean;
  instructor_info: { instructors: Instructor[] };
  intro_video: string | null;
  language: string | null;
  learning_info: string[];
  license: string | null;
  org: string;
  overview: string;
  pre_requisite_courses: string[];
  run: string;
  self_paced: boolean;
  short_description: string;
  start_date: string | null;
  subtitle: string;
  syllabus: string | null;
  title: string;
  video_thumbnail_image_asset_path: string;
  video_thumbnail_image_name: string;
}

/**
 * "CertificatesDisplayBehaviors.END" → "end".
 *
 * See hazard 3 in the file header. Anything already in short form passes through, so
 * this stays correct if the platform fixes its serialisation.
 */
export function normaliseDisplayBehaviour(value: string | null): CertificatesDisplayBehaviour {
  const tail = (value ?? '').split('.').pop() ?? '';
  const candidate = tail.toLowerCase();
  return candidate === 'end_with_date' || candidate === 'early_no_info' ? candidate : 'end';
}

export const getCourseSettings = (courseKey: string) =>
  openedx.get<CourseSettings>(`${CONTENTSTORE}/course_settings/${seg(courseKey)}`);

export async function getCourseDetails(courseKey: string): Promise<CourseDetails> {
  const raw = await openedx.get<CourseDetails>(`${CONTENTSTORE}/course_details/${seg(courseKey)}`);
  return {
    ...raw,
    certificates_display_behavior: normaliseDisplayBehaviour(raw.certificates_display_behavior),
    // Older courses can carry nulls where the page expects collections.
    instructor_info: { instructors: raw.instructor_info?.instructors ?? [] },
    learning_info: raw.learning_info ?? [],
    pre_requisite_courses: raw.pre_requisite_courses ?? [],
  };
}

/**
 * Save the schedule & details.
 *
 * PUT, and always the WHOLE object — see hazard 2. Callers must pass the loaded payload
 * with their edits merged in, never a diff. `openedx` has no `put`, so this drops to
 * `request`, which still carries CSRF, the JWT refresh and the single retry.
 */
export const saveCourseDetails = (courseKey: string, details: CourseDetails) =>
  request<CourseDetails>(`${CONTENTSTORE}/course_details/${seg(courseKey)}`, {
    method: 'PUT',
    json: details,
  });

/** `[code, label]` pairs for a select, with the index guards strict mode wants. */
export function languageOptions(settings: CourseSettings | undefined): [string, string][] {
  return (settings?.language_options ?? []).map((pair) => {
    const code = pair[0] ?? '';
    return [code, pair[1] ?? code];
  });
}

/* ------------------------------------------------------------------- grading ------- */

export interface Grader {
  /** Index in the graders list, assigned by the server. Not stable across saves. */
  id: number;
  type: string;
  min_count: number;
  drop_count: number;
  short_label: string | null;
  /** A PERCENTAGE (0–100) in both directions; the server divides by 100 on write. */
  weight: number;
}

export interface GracePeriod {
  hours: number;
  minutes: number;
}

export interface GradingModel {
  graders: Grader[];
  /** Letter → fraction of 1, e.g. { A: 0.75 }. Not percentages, unlike grader weight. */
  grade_cutoffs: Record<string, number>;
  grace_period: GracePeriod | null;
  minimum_grade_credit: number;
}

export interface CourseGradingContext {
  mfe_proctored_exam_settings_url?: string | null;
  /** Assignment type → subsections using it. Read-only, and the reason a delete warns. */
  course_assignment_lists: Record<string, string[]>;
  /** The editable model is NESTED here, not at the top level of the response. */
  course_details: GradingModel;
  show_credit_eligibility: boolean;
  is_credit_course: boolean;
  default_grade_designations: string[];
}

export const getCourseGrading = (courseKey: string) =>
  openedx.get<CourseGradingContext>(`${CONTENTSTORE}/course_grading/${seg(courseKey)}`);

/**
 * Save the grading policy.
 *
 * POST to the same path, with the model at the TOP level — the nesting under
 * `course_details` is a read-only convenience of the GET. All four keys are required
 * (hazard 2): the server indexes them directly, so a missing one is a 500, not a 400.
 */
export const saveCourseGrading = (courseKey: string, model: GradingModel) =>
  openedx.post<GradingModel>(`${CONTENTSTORE}/course_grading/${seg(courseKey)}`, model);

export const WEIGHT_TOTAL = 100;

/**
 * Grader weights are a percentage split of the final grade.
 *
 * The platform does NOT enforce the total. It stores whatever arrives, divided by 100,
 * and every learner's grade is then computed against a scale that does not add up — so
 * a course can look configured and grade wrongly for a whole run. Callers validate with
 * this before enabling a save.
 */
export function totalWeight(weights: number[]): number {
  // Rounded because 0.29 * 100 style float error would otherwise make a legitimate
  // 100 read as 99.99999999999999.
  return Math.round(weights.reduce((sum, weight) => sum + weight, 0) * 100) / 100;
}

/* -------------------------------------------------------------- certificates ------- */

export interface CertificateSignatory {
  id: number;
  name: string;
  organization?: string;
  signature_image_path: string;
  title: string;
}

export interface CourseCertificate {
  course_title?: string;
  description: string;
  editing?: boolean;
  id: number;
  is_active: boolean;
  name: string;
  signatories: CertificateSignatory[];
  version: number;
}

export interface CourseCertificatesContext {
  /** Legacy session-only Studio handler. Not callable from here — see the page. */
  certificate_activation_handler_url: string;
  certificate_web_view_url: string | null;
  certificates: CourseCertificate[] | null;
  course_modes: string[];
  /** False when no enrolment mode can bear a certificate. Then there is nothing to configure. */
  has_certificate_modes: boolean;
  is_active: boolean;
  is_global_staff: boolean;
  mfe_proctored_exam_settings_url?: string | null;
  course_number: string;
  course_title: string;
  course_number_override: string;
}

export const getCourseCertificates = (courseKey: string) =>
  openedx.get<CourseCertificatesContext>(`${CONTENTSTORE}/certificates/${seg(courseKey)}`);

/* ----------------------------------------------------------------- plumbing -------- */

/**
 * The course key for the page, from the route or from `?course=`.
 *
 * Pure, so it works whichever way the route is eventually registered — `:courseId`
 * matches the learner routes' existing param name, `:courseKey` reads better on an
 * admin route, and the query string covers a flat route with no param at all.
 */
export function resolveCourseKey(
  params: Readonly<Partial<Record<string, string | undefined>>>,
  search: URLSearchParams,
): string | null {
  return params['courseKey'] ?? params['courseId'] ?? search.get('course') ?? null;
}

/**
 * Turn a failure into something an admin can act on.
 *
 * `body.error` is what contentstore returns for a rejected save (bad date format,
 * invalid prerequisite key), and it is the most useful sentence available — but only if
 * it is surfaced. The status-code cases exist because "403" and "the course was moved"
 * need different actions from the reader.
 */
export function explainFailure(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'The request never reached Studio. Check your connection, then try again.';
  }

  const body = error.body as { error?: unknown; detail?: unknown } | null;
  const detail =
    typeof body?.error === 'string' ? body.error
    : typeof body?.detail === 'string' ? body.detail
    : null;

  switch (error.status) {
    case 400:
      return detail ?? 'Studio rejected these values. Check the dates and numbers, then save again.';
    case 403:
      return (
        detail ??
        'You do not have permission to change this course. Ask an administrator for author access to it.'
      );
    case 404:
      return 'This course no longer exists, or its ID has changed. Reopen it from the course list.';
    case 500:
    case 502:
    case 503:
      return 'Studio failed while handling the request. Nothing was saved — retry in a moment, and tell an administrator if it persists.';
    default:
      return detail ?? `Studio responded with ${error.status}. Nothing was saved.`;
  }
}

/* ------------------------------------------------------------ date plumbing -------- */

/**
 * ISO instant → the value `<input type="datetime-local">` expects.
 *
 * The input has no time zone, so it must show LOCAL wall-clock time; formatting the ISO
 * string directly would display UTC and shift every date an author reads by their
 * offset.
 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Local wall-clock back to an ISO instant. Empty means "no date", which is a valid value. */
export function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Human date for read-only context, e.g. the upgrade deadline. */
export function formatInstant(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
